import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaProjectDiagram, FaLink, FaSearch, FaImage, FaTimes } from 'react-icons/fa';
import { supabase } from '../lib/supabase';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', link: '', status: 'Berjalan', banner_image: '' });
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerInputRef = useRef(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    setLoading(true);
    const { data } = await supabase.from('projects').select('*').order('created_at', { ascending: false });
    setProjects(data || []);
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ title: '', description: '', link: '', status: 'Berjalan', banner_image: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (p) => {
    setForm({ title: p.title, description: p.description || '', link: p.link || '', status: p.status || 'Berjalan', banner_image: p.banner_image || '' });
    setEditing(p.id);
    setShowForm(true);
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingBanner(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `project-banner-${Date.now()}.${fileExt}`;
      const filePath = `alumni-photos/${fileName}`;
      const { error: uploadError } = await supabase.storage
        .from('alumni-photos')
        .upload(filePath, file, { contentType: file.type, cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('alumni-photos').getPublicUrl(filePath);
      setForm(prev => ({ ...prev, banner_image: urlData.publicUrl }));
    } catch (err) {
      alert('Gagal upload banner: ' + (err.message || 'Coba lagi.'));
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Judul proyek harus diisi');
    setSaving(true);
    if (editing) {
      await supabase.from('projects').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editing);
    } else {
      await supabase.from('projects').insert([form]);
    }
    setSaving(false);
    resetForm();
    fetchProjects();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus proyek ini?')) return;
    await supabase.from('projects').delete().eq('id', id);
    fetchProjects();
  };

  const filtered = projects.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const normalizeUrl = (url) => {
    if (!url) return url;
    if (!/^https?:\/\//i.test(url)) return 'https://' + url;
    return url;
  };

  const statusColor = (s) => {
    if (s === 'Berjalan') return 'bg-green-100 text-green-700';
    if (s === 'Selesai') return 'bg-blue-100 text-blue-700';
    if (s === 'Rencana') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <FaProjectDiagram className="text-gold-400" /> Kelola Proyek
            </h1>
            <p className="text-gray-300">Program kerja & proyek ikatan alumni PIMNAS</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-sm"
          >
            <FaPlus /> Tambah Proyek
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form Modal */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {editing ? 'Edit Proyek' : 'Tambah Proyek Baru'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Judul Proyek *</label>
                <input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Nama program kerja / proyek"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Deskripsi</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Deskripsi singkat proyek"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Banner Gambar</label>
                {form.banner_image ? (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 mb-2">
                    <img src={form.banner_image} alt="Banner" className="w-full h-48 object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, banner_image: '' })}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg transition-colors"
                    >
                      <FaTimes className="text-xs" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => bannerInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-primary-400 hover:text-primary-500 cursor-pointer transition-colors"
                  >
                    {uploadingBanner ? (
                      <div className="w-8 h-8 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <FaImage className="text-3xl mb-2" />
                        <span className="text-sm font-medium">Klik untuk upload banner</span>
                        <span className="text-xs mt-1">JPG, PNG (Rekomendasi 1200x400)</span>
                      </>
                    )}
                  </div>
                )}
                <input type="file" accept="image/*" ref={bannerInputRef} onChange={handleBannerUpload} className="hidden" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Link Proyek (URL Eksternal)</label>
                <input
                  value={form.link}
                  onChange={e => setForm({ ...form, link: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="https://contoh-website.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option>Rencana</option>
                  <option>Berjalan</option>
                  <option>Selesai</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Menyimpan...' : editing ? 'Simpan Perubahan' : 'Tambah Proyek'}
              </button>
              <button
                onClick={resetForm}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium py-2.5 px-6 rounded-lg transition-colors"
              >
                Batal
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Cari proyek..."
          />
        </div>

        {/* Project List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FaProjectDiagram className="text-5xl mx-auto mb-4 opacity-50" />
            <p>Belum ada proyek.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map(p => (
              <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow overflow-hidden">
                {p.banner_image && (
                  <img src={p.banner_image} alt={p.title} className="w-full h-40 object-cover" />
                )}
                <div className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{p.title}</h3>
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusColor(p.status)}`}>
                          {p.status}
                        </span>
                      </div>
                      {p.description && (
                        <p className="text-sm text-gray-500 mb-2 line-clamp-2">{p.description}</p>
                      )}
                      {p.link && (
                        <a
                          href={normalizeUrl(p.link)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          <FaLink className="text-xs" /> {p.link.replace(/https?:\/\/(www\.)?/, '').slice(0, 50)}
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(p)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Hapus"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProjects;
